/**
 * @Page GrpPtno
 * @Description 품목그룹등록(RST03) > 품목 추가/삭제 팝업
 *
 * 동작 방식:
 *  1. 회사/공장을 선택하고 조회하면 /rst03/wapt/search로 해당 공장의 등록 품번을 로드
 *  2. ROM4L 품목 목록 조회 후 회사+공장+품번이 일치하는 행을 자동 체크
 *  3. 확인 클릭 시:
 *     - 새로 체크된 항목(현재 선택 ∩ ∉ existingSet) → /rst03/wapt/insert
 *     - 기존에 있었으나 체크 해제된 항목(existingSet ∩ ∉ 현재 선택) → /rst03/wapt/delete
 *  4. 완료 후 현재 회사/공장 목록을 다시 조회
 **/
import React, { useState, useEffect, useRef, useCallback } from "react";
import ContentXearch from '@/components/organisms/ContentXearch';
import Grid from '@/components/grid/agGrid';
import CustomAlert from "@/components/molecules/CustomAlert";
import * as Common from '@/utils/Common';

const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER;
const membershipKey = (comp, plnt, ptno) => `${comp || ""}|${plnt || ""}|${ptno || ""}`;

function GrpPtno({ onClose, grtp, vnt1 }) {

    const token = Common.getToken();
    const userId = Common.getToken("id");
    const tComp = Common.getToken("comp");

    const grdGrpPtno = useRef();

    // ─── 알림 ──────────────────────────────────────────────────────────
    const [message, setMessage] = useState({ openAlert: false, severity: "success", msg: "" });
    const { openAlert, severity, msg } = message;

    // ─── 기존 등록 품번 Set (WRTWAPT) ──────────────────────────────────
    const [existingSet, setExistingSet] = useState(new Set()); // 초기 등록된 회사+공장+품번
    const pendingPreSelect = useRef(false);                    // 조회 후 사전체크 필요 여부
    const latestExistingSet = useRef(new Set());               // 최신 existingSet (비동기 문제 우회용)
    const latestSearchContext = useRef({ comp: "", plnt: "" });

    // ─── 검색 조건 상태 ─────────────────────────────────────────────────
    const [idata, setData] = useState({
        kVnt1: vnt1 || Common.getToken("vand"),
        kComp: tComp,
        kPlnt: "",
        kPtno: "",
        kEmgubn: "1",   // 1=전체, 2=사용, 3=미사용
        vnt1List: [],
        plntList: [],
    });

    let [searchApi, setSearchApi] = useState(false);
    const [clearGrid, setClearGrid] = useState(false);

    const onClearApi = useCallback(() => {
        setSearchApi(false);
        // 조회 완료 후 사전 체크 실행 (gridApi.setRowData 이후 렌더링 대기)
        if (pendingPreSelect.current) {
            pendingPreSelect.current = false;
            setTimeout(() => {
                if (grdGrpPtno.current?.selectRowsByCondition) {
                    const { comp, plnt } = latestSearchContext.current;
                    grdGrpPtno.current.selectRowsByCondition(row =>
                        latestExistingSet.current.has(membershipKey(comp, row.wdcplnt || plnt, row.wdcptno))
                    );
                }
            }, 300);
        }
    }, []);

    // ─── 마운트: 공통코드 + 기존 WRTWAPT 목록 로드 ─────────────────────
    useEffect(() => {
        onSearchCommon(`{"pgmid":"VNT1"}`, "vnt1List");
        onSearchCommon(
            `{"pgmid":"PLNT","kVnt1":"${idata.kVnt1}","kComp":"${idata.kComp}","kGubn":""}`,
            "plntList"
        );
    }, []);

    /**
     * grtp에 이미 등록된 품번 목록 조회 → existingSet에 저장
     */
    const loadExistingWapt = async (comp, plnt) => {
        const para = `{"kVnt1":"${idata.kVnt1}","grtp":"${grtp}","comp":"${comp}","plnt":"${plnt}","gryn":"Y"}`;
        const param = encodeURI(para);
        const query = SERVER_URL + "/rst03/wapt/search?query=" + param;
        let resultSet = new Set();
        await fetch(query, { headers: { Authorization: token } })
            .then(resp => resp.json())
            .then(data => {
                if (data.success && data.data) {
                    resultSet = new Set(data.data.map(item => membershipKey(
                        item.comp || item.wrccomp,
                        item.plnt || item.wrcplnt,
                        item.ptno || item.wrcptno
                    )));
                    setExistingSet(resultSet);
                    latestExistingSet.current = resultSet; // ref에도 즉시 반영
                }
            })
            .catch(() => { });
        return resultSet;
    };

    const onSearchCommon = (para, name) => {
        const fetchData = async () => {
            let param = encodeURIComponent(para);
            const query = SERVER_URL + "/comcd/search?query=" + param;
            await fetch(query, { headers: { Authorization: token } })
                .then(resp => resp.json())
                .then(data => {
                    if (data.success) {
                        setData(prev => {
                            const next = { ...prev };
                            if (name === "plntList" && data.data.length > 0 && !prev.kPlnt) {
                                next.kPlnt = data.data[0].comCode;
                            }
                            next[name] = data.data;
                            return next;
                        });
                    }
                });
        };
        fetchData();
    };

    // ─── 조건 변경 ─────────────────────────────────────────────────────
    const onChange = async (e) => {
        const { name, value } = e.target;

        if (["kVnt1", "kComp", "kPlnt"].includes(name)) {
            setExistingSet(new Set());
            latestExistingSet.current = new Set();
            setClearGrid(true);
        }

        setData(prev => {
            const next = { ...prev, [name]: value };
            if (name === "kVnt1" || name === "kComp") {
                next.kPlnt = "";
                next.plntList = [];
                onSearchCommon(
                    `{"pgmid":"PLNT","kVnt1":"${name === "kVnt1" ? value : prev.kVnt1}","kComp":"${name === "kComp" ? value : prev.kComp}","kGubn":""}`,
                    "plntList"
                );
            }
            return next;
        });
    };

    // ─── ROM4L 품목 조회 ────────────────────────────────────────────────
    const onSearch = async () => {
        if (!idata.kPlnt) {
            setMessage({ openAlert: true, severity: "info", msg: "공장을 선택하십시요!" });
            return;
        }
        await loadExistingWapt(idata.kComp, idata.kPlnt);
        latestSearchContext.current = { comp: idata.kComp, plnt: idata.kPlnt };
        const para = `{"kVnt1":"${idata.kVnt1}","kComp":"${idata.kComp}","kPlnt":"${idata.kPlnt}","kPtno":"${idata.kPtno}","kEmgubn":"${idata.kEmgubn}","_refresh":"${Date.now()}"}`;
        const param = encodeURIComponent(para);
        const query = SERVER_URL + "/rom4l/search?query=" + param;
        pendingPreSelect.current = true; // 조회 완료 후 사전 체크 플래그
        setSearchApi(query);
    };

    // ─── 확인: insert(신규 체크) + delete(체크 해제) 처리 ───────────────
    const onConfirm = async (e) => {
        // if (e) { e.preventDefault(); e.stopPropagation(); }
        const selectedNodes = grdGrpPtno.current?.getSelectedNodes() || [];

        // 현재 선택된 회사+공장+품번 Set
        const currentSet = new Set(selectedNodes.map(n =>
            membershipKey(idata.kComp, n.data.wdcplnt || idata.kPlnt, n.data.wdcptno)
        ));

        // 신규 INSERT: 현재 선택 O & 기존 Set X
        const currentExistingSet = latestExistingSet.current;
        const toInsert = selectedNodes
            .filter(n => !currentExistingSet.has(membershipKey(idata.kComp, n.data.wdcplnt || idata.kPlnt, n.data.wdcptno)))
            .map(n => ({
                vand: idata.kVnt1,
                grtp: grtp,
                comp: idata.kComp,
                plnt: n.data.wdcplnt || idata.kPlnt,
                ptno: n.data.wdcptno,
                ptnm: n.data.wdcptnm,
            }));

        // DELETE: 그리드에 표시된 행 중, 기존 등록 O & 현재 체크 해제 X
        // ※ existingSet 전체와 비교하면 그리드에 없는 품번도 삭제 대상에 포함되므로
        //    반드시 현재 그리드 rowData 기준으로 필터링해야 함
        const allGridRows = grdGrpPtno.current?.getRowAllData() || [];
        const toDelete = allGridRows
            .filter(row => {
                const key = membershipKey(idata.kComp, row.wdcplnt || idata.kPlnt, row.wdcptno);
                return currentExistingSet.has(key) && !currentSet.has(key);
            })
            .map(row => ({
                vand: idata.kVnt1,
                grtp: grtp,
                comp: idata.kComp,
                plnt: row.wdcplnt || idata.kPlnt,
                ptno: row.wdcptno,
            }));

        if (toInsert.length === 0 && toDelete.length === 0) {
            setMessage({ openAlert: true, severity: "info", msg: "변경된 항목이 없습니다." });
            return;
        }

        let hasError = false;
        const errors = [];

        // INSERT
        if (toInsert.length > 0) {
            await fetch(SERVER_URL + "/rst03/wapt/insert", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: token },
                body: JSON.stringify(toInsert),
            })
                .then(resp => resp.json())
                .then(data => {
                    console.log("data>>>>")
                    console.log(data)
                    if (!data.success) {
                        hasError = true;
                        errors.push(data.errors?.message || "품목 등록 오류");
                    }
                })
                .catch(() => { hasError = true; errors.push("품목 등록 중 서버 오류가 발생했습니다."); });
        }

        // DELETE
        if (toDelete.length > 0) {
            await fetch(SERVER_URL + "/rst03/wapt/delete", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: token },
                body: JSON.stringify(toDelete),
            })
                .then(resp => resp.json())
                .then(data => {
                    if (!data.success) {
                        hasError = true;
                        errors.push(data.errors?.message || "품목 삭제 오류");
                    }
                })
                .catch(() => { hasError = true; errors.push("품목 삭제 중 서버 오류가 발생했습니다."); });
        }

        if (hasError) {
            setMessage({ openAlert: true, severity: "error", msg: errors.join(" / ") });
            return;
        }

        const insertCnt = toInsert.length;
        const deleteCnt = toDelete.length;
        setMessage({
            openAlert: true, severity: "success",
            msg: `완료: 추가 ${insertCnt}건, 삭제 ${deleteCnt}건`,
        });

        // 팝업을 닫지 않고 현재 회사/공장 기준으로 재조회
        await onSearch();
        onClose(true);
    };

    // 품번 외에 품명/공장 셀을 클릭해도 선택된 체크 상태를 유지합니다.
    // 해제는 체크박스를 직접 클릭할 때만 수행합니다.
    const onItemCellClicked = e => {
        if (e?.column?.getColId() !== "wdcptno" && !e.node.isSelected()) {
            e.node.setSelected(true);
        }
    };

    // ─── 검색조건 ───────────────────────────────────────────────────────
    const optionsX = {
        0: [
            {
                formatter: "select", name: "kVnt1", labelName: "T1업체", type: "text",
                defaultValue: idata.kVnt1, codeMode: true, selectAll: "",
                codes: null, comms: idata.vnt1List, align: "left",
            },
            {
                formatter: "select", name: "kPlnt", labelName: "공장", type: "text",
                defaultValue: idata.kPlnt, codeMode: false, selectAll: "",
                codes: null, comms: idata.plntList,
            },
            { formatter: "blank", align: "right" },
        ],
        1: [
            // {
            //     formatter: "select", name: "kEmgubn", labelName: "사용여부", type: "text",
            //     defaultValue: idata.kEmgubn, codeMode: false, selectAll: "",
            //     codes: [
            //         { text: "전체", comCode: "1" },
            //         { text: "사용", comCode: "2" },
            //         { text: "미사용", comCode: "3" },
            //     ],
            //     comms: null, align: "left",
            // },
            {
                formatter: "input", name: "kPtno", labelName: "품번", type: "text",
                defaultValue: idata.kPtno, readOnly: false, align: "left", labelType: "none",
            },
            { formatter: "blank", align: "right" },
        ],
    };

    // ─── Grid 컬럼 ──────────────────────────────────────────────────────
    const columnDefs = [
        {
            headerName: "품번", field: "wdcptno",
            sortable: true, filter: true, flex: 2, minWidth: 140,
            editable: false, cellClass: ["nocheckbox", "alignL"],
            checkboxSelection: true, headerCheckboxSelection: true,
        },
        {
            headerName: "품명", field: "wdcptnm",
            sortable: true, filter: true, flex: 3, minWidth: 200,
            editable: false, cellClass: ["nocheckbox", "alignL"],
        },
        {
            headerName: "공장", field: "wdcplnt",
            sortable: true, filter: true, flex: 1, minWidth: 80,
            editable: false, cellClass: ["nocheckbox", "alignC"],
        },
        // {
        //     headerName: "사용여부", field: "wdcflag",
        //     sortable: true, filter: true, flex: 1, minWidth: 80,
        //     editable: false, cellClass: ["nocheckbox", "alignC"],
        //     valueFormatter: params => params.value === "Y" ? "사용" : params.value === "N" ? "미사용" : params.value ?? "",
        // },
    ];

    // ─── Render ─────────────────────────────────────────────────────────
    return (
        <>
            {/* Alert */}
            <CustomAlert
                open={openAlert} severity={severity} duration={3000} message={msg}
                onClose={() => setMessage({ openAlert: false, severity: "", msg: "" })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            />

            {/* 헤더 */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                <b style={{ color: "#cd5c5c", fontFamily: "PretendardLight, NanumGothic, sans-serif", fontSize: "14px" }}>
                    품목 추가/삭제 - 그룹코드 [{grtp}]
                </b>
                <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                     {/* (체크 = 등록, 해제 = 삭제) */}
                    {/* 닫기 */}
                    <button
                        onClick={() => onClose(false)}
                        style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0 }}
                    >
                        <img src="/images/button/close_button.png" alt="close" />
                    </button>
                </div>
            </div>

            {/* 검색조건 */}
            <ContentXearch optionsX={optionsX} onChange={onChange} onClick={onSearch} />

            {/* 버튼 바 */}
            <div style={{
                display: "flex", flexFlow: "row nowrap", justifyContent: "flex-end",
                alignItems: "center", height: "50px",
                borderTop: "1px solid #BFBDCD", borderBottom: "1px solid #BFBDCD",
                background: "#fff", paddingRight: "5px",
            }}>
                <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSearch(); }}
                    style={{
                        display: "flex", flexFlow: "row nowrap", alignItems: "center",
                        height: "30px", border: "1px solid #B7BCD6", borderRadius: "2px",
                        background: "#fff", marginLeft: "10px", fontWeight: 600,
                        cursor: "pointer", padding: "0 10px 0 8px", fontSize: "13px",
                        fontFamily: "PretendardLight, NanumGothic, sans-serif",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#E8ECF5"}
                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                >
                    <img src="/images/button/btn_search.png" alt="Search" style={{ marginRight: "6px", marginLeft: "2px" }} />
                    <span>조회</span>
                </button>
                <a
                    type="button"
                    onClick={onConfirm}
                    style={{
                        display: "flex", flexFlow: "row nowrap", alignItems: "center",
                        height: "30px", border: "1px solid #B7BCD6", borderRadius: "2px",
                        background: "#fff", marginLeft: "10px", fontWeight: 600,
                        cursor: "pointer", padding: "0 10px 0 8px", fontSize: "13px",
                        fontFamily: "PretendardLight, NanumGothic, sans-serif",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#E8ECF5"}
                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                >
                    <img src="/images/button/btn_insert.png" alt="insert" style={{ marginRight: "6px", marginLeft: "2px" }} />
                    <span>입력</span>
                </a>
            </div>

            {/* 안내 문구 */}
            <div style={{
                background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: "4px",
                padding: "5px 10px", marginTop: "4px", marginBottom: "4px",
                fontSize: "12px", color: "#1565c0",
            }}>
                📣 이미 등록된 품번은 <b>체크됨</b>으로 표시됩니다. 등록 삭제여부에 따라 체크 변경 후 <b>✔ 입력</b>을 클릭하세요.
            </div>

            {/* 그리드 */}
            <div className={"grid_type01"}>
                <div className={"grid_wrap01"}>
                    <div className="grid_cont_box" style={{ height: "360px", width: "100%" }}>
                        <Grid
                            ref={grdGrpPtno}
                            gridId={"GRPPTNO"}
                            columnDefs={columnDefs}
                            pagination={false}
                            paginationCsv={false}
                            paginationPageSize={"1000"}
                            rowMultiSelectWithClick={false}
                            suppressRowClickSelection={true}
                            rowSelection={"multiple"}
                            enableRangeSelection={false}
                            enableCellTextSelection={true}
                            isFirst={true}
                            displayNo={false}
                            searchApi={searchApi}
                            onClearApi={onClearApi}
                            onCellClickEvent={onItemCellClicked}
                            clearGrid={clearGrid}
                            onClearGridFlag={setClearGrid}
                            onCellValueChangedEvent={async () => { }}
                            useMessage={false}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default GrpPtno;
